import React, { PureComponent } from 'react';
import flow from 'lodash/flow';

import CategoryMeta from './components/CategoryMeta';
import Loading from '../../shared/Loading';
import getSearchQueryParams from '../../utils/getSearchQueryParams';
import NotFound from '../../shared/NotFound';
import { trackPageVisit, PAGE_NAME, PAGE_PARAMS } from '../../utils/tracking';
import eventResolver from '../../state/resolvers/event';
import { BrazeCustomEvents, logCustomEvent } from '../../state/services/braze';
import { withBrowserInfo } from '../../hocs';
import { withTracking } from './provider';
import CategoryPage from './component';

const RESULTS_PER_PAGE = 24;

// Wrap the util with a reultsPerPage value, to override default
const getSearchQueryParamsWithSize = (query, location) => ({
  ...getSearchQueryParams({
    ...query,
    locationName:
      location && (location.city || location.state || location.country),
  }),
  resultsPerPage: RESULTS_PER_PAGE,
});

const addPaginationToCanonical = (categoryData, page) => {
  try {
    const categoryDataCopy = { ...categoryData };

    if (page && !categoryDataCopy.path.includes('page') && page !== 1) {
      categoryDataCopy.path = `${categoryDataCopy.path}?page=${page}`;
    }

    return categoryDataCopy;
  } catch (exc) {
    return categoryData;
  }
};

const getGraphQLParams = (urlParams) => {
  const {
    localeSlug,
    locationSlug,
    sportSlug,
    tagSlug,
    page,
    resultsPerPage,
  } = urlParams;
  return {
    countrySlug: localeSlug ? localeSlug.toLowerCase() : 'us', // Required. i.e. "US"
    locationSlug, // i.e. "california"
    sportSlug, // i.e. "running"
    tagSlug, // i.e. "ironman" or "10k"
    page,
    resultsPerPage,
  };
};

class CategoryPageContainer extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isFiltersActive: false,
    };
  }

  componentDidMount() {
    this.trackVisit();
  }

  trackVisit = () => {
    trackPageVisit(PAGE_NAME.CATEGORY_PAGE, {
      [PAGE_PARAMS.CATEGORY_SLUGS]: getGraphQLParams(this.props.params),
    });
    logCustomEvent(BrazeCustomEvents.CATEGORY_VISITED, {
      ...getGraphQLParams(this.props.params),
    });
  };

  // eslint-disable-next-line no-unused-vars
  handleFiltersSubmit = (formValues, fieldChanged) => {};

  toggleFilters = (isFiltersActive) => this.setState({ isFiltersActive });

  render() {
    const {
      data: { categoryData },
      loading: dataLoading,
      params: { page, resultsPerPage },
      userAgent,
      router,
      injected: {
        tracking: { trackButtonClick },
      },
    } = this.props;

    if (!categoryData && dataLoading) return <Loading />;
    if (!categoryData) return <NotFound route={this.props.route} />;

    const rawEventsResult = categoryData.eventsV2 || categoryData.events || {};

    const events =
      (rawEventsResult.events && rawEventsResult.events.map(eventResolver)) ||
      [];

    const metaProps = {
      categoryData: addPaginationToCanonical(categoryData, page),
      events,
      page,
      totalResults: rawEventsResult.total,
      resultsPerPage,
      router,
    };

    const { content: { pageInfo = {} } = {} } = categoryData;

    const componentProps = {
      trackingSource: pageInfo.title,
      query: getSearchQueryParamsWithSize(
        categoryData.query,
        categoryData.location,
      ),
      categoryData,
      currentPage: page,
      resultsPerPage,
      userAgent,
      onDescriptionExpand: () => trackButtonClick('description_read_more'),
      dataLoading,
    };

    return (
      <>
        <CategoryMeta {...metaProps} />
        <CategoryPage
          onFiltersSubmit={this.handleFiltersSubmit}
          onToggleView={this.props.dispatchToggleMapView}
          onFiltersToggle={this.toggleFilters}
          isMapActive={this.props.isMapActive}
          isFiltersActive={this.state.isFiltersActive}
          showFooter
          {...componentProps}
        />
      </>
    );
  }
}

export default flow(
  withBrowserInfo,
  withTracking('page'),
)(CategoryPageContainer);
